import React, { useState, useEffect } from 'react';

import Observer from '@researchgate/react-intersection-observer';
import FocusTrap from 'focus-trap-react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import { useTransition, animated } from 'react-spring';
import styled from 'styled-components';

import {
  HeaderAttachment,
  useBreakpoint,
} from '~/techstyle-shared/react-components';

import { SelectedFacetsToApplyProvider } from '../../hooks/useSelectedFacetsToApply';

import MobileGridFiltersButton from './MobileGridFiltersButton';
import MobileGridFiltersPane from './MobileGridFiltersPane';
import MobileGridFloatingFilter from './MobileGridFloatingFilter';

const Overlay = styled(animated.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 40;
`;

const Drawer = styled(animated.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  max-width: 280px;
  max-height: 100vh;
  background: white;
  z-index: 50;
  display: flex;
  flex-direction: column;
  outline: none;
`;

const MobileGridFilters = ({ isFilterDrawerOpen, setIsFilterDrawerOpen }) => {
  const [showFloatingFilter, setShowFloatingFilter] = useState(false);
  const { isMobile } = useBreakpoint();

  // Escape key closes drawer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFilterDrawerOpen(false);
      }
    };

    if (isFilterDrawerOpen) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    }

    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterDrawerOpen]);

  const transitions = useTransition(isFilterDrawerOpen, {
    from: { opacity: 0, transform: 'translateX(100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(100%)' },
    config: { tension: 400, friction: 26, clamp: true },
  });

  /**
   * HeaderAttachment breaks out of the normal DOM and attaches its child node to the nav header ref.
   * This bypasses the isMobile logic that is used to determine mobile vs. desktop grid layout, so we
   * have to do an explicit isMobile check, even this MobileGridFilters component is used for mobile only
   * */
  const handleIntersection = (event) => {
    const { isIntersecting } = event;
    if (isMobile) {
      setShowFloatingFilter(!isIntersecting);
    }
  };

  return (
    <SelectedFacetsToApplyProvider>
      {isMobile && (
        <HeaderAttachment>
          <MobileGridFloatingFilter
            isVisible={showFloatingFilter}
            onClick={() => setIsFilterDrawerOpen(true)}
          />
        </HeaderAttachment>
      )}
      <div style={{ paddingRight: '16px' }}>
        <Observer onChange={handleIntersection}>
          <MobileGridFiltersButton
            isOpen={isFilterDrawerOpen}
            setIsOpen={setIsFilterDrawerOpen}
          />
        </Observer>

        {transitions((style, item) =>
          item
            ? ReactDOM.createPortal(
                <FocusTrap active={isFilterDrawerOpen}>
                  <RemoveScroll
                    enabled={isFilterDrawerOpen}
                    shards={[() => document.getElementById('facet-scrollable')]}
                    allowPinchZoom
                  >
                    <>
                      <Drawer
                        role="dialog"
                        aria-modal="true"
                        aria-label="Mobile Filters"
                        data-drawer-dialog=""
                        tabIndex={-1}
                        style={style}
                      >
                        <SelectedFacetsToApplyProvider>
                          <MobileGridFiltersPane
                            handleClose={() => setIsFilterDrawerOpen(false)}
                          />
                        </SelectedFacetsToApplyProvider>
                      </Drawer>
                      <Overlay
                        style={{ opacity: style.opacity }}
                        onClick={() => setIsFilterDrawerOpen(false)}
                      />
                    </>
                  </RemoveScroll>
                </FocusTrap>,
                document.body
              )
            : null
        )}
      </div>
    </SelectedFacetsToApplyProvider>
  );
};

MobileGridFilters.propTypes = {
  isFilterDrawerOpen: PropTypes.bool.isRequired,
  setIsFilterDrawerOpen: PropTypes.func.isRequired,
};

export default MobileGridFilters;
